// automationValueUtils.js

import { normalizeEmailValueForStorage } from 'utilities/Email';
import { isEnumCompatibleFieldType } from 'utilities/FieldTypes';
import { normalizePhoneValueForStorage } from 'utilities/Phone';
import { createRichTextValue } from 'utilities/RichText';

export const tagsLiteralToText = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry ?? '').trim()).filter(Boolean).join(', ');
  }
  return String(value ?? '');
};

export const peopleLiteralToUserKeys = (value) => {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  return list
    .map((entry) => {
      if (entry == null) return '';
      if (typeof entry === 'string' || typeof entry === 'number') return String(entry).trim();
      return String(entry.userKey || entry.key || '').trim();
    })
    .filter(Boolean);
};

export const peopleLiteralToText = (value, baseMembers = []) => {
  const keys = peopleLiteralToUserKeys(value);
  if (!keys.length) return '';

  const members = Array.isArray(baseMembers) ? baseMembers : [];
  if (!members.length) return keys.join(', ');

  return keys.map((userKey) => {
    const match = members.find((member) => {
      const memberKey = String(member?.userKey || member?.key || member?.id || '').trim();
      return memberKey === userKey;
    });
    return String(
      match?.title
      || match?.name
      || match?.user?.name
      || userKey,
    ).trim() || userKey;
  }).join(', ');
};

export const parseLiteral = (value, fieldType, enumOptions = [], operator = '') => {
  const normalizedOperator = String(operator || '').trim().toLowerCase();

  if (fieldType === 'date' || fieldType === 'datetime' || fieldType === 'daterange' || fieldType === 'datetimerange') {
    const normalizeDurationUnit = (rawUnit) => {
      const normalizedUnit = String(rawUnit || '').trim().toLowerCase();
      if (normalizedUnit === 'day' || normalizedUnit === 'days') return 'days';
      if (normalizedUnit === 'week' || normalizedUnit === 'weeks') return 'weeks';
      if (normalizedUnit === 'month' || normalizedUnit === 'months') return 'months';
      if (normalizedUnit === 'year' || normalizedUnit === 'years') return 'years';
      return '';
    };

    const normalizeCalendarUnit = (rawUnit) => {
      const normalizedUnit = String(rawUnit || '').trim().toLowerCase();
      if (normalizedUnit === 'week' || normalizedUnit === 'weeks') return 'week';
      if (normalizedUnit === 'month' || normalizedUnit === 'months') return 'month';
      if (normalizedUnit === 'year' || normalizedUnit === 'years') return 'year';
      return '';
    };

    if (normalizedOperator === 'is_empty' || normalizedOperator === 'is_not_empty') {
      return null;
    }

    if (fieldType === 'date' || fieldType === 'datetime') {
      if (normalizedOperator === 'within_past' || normalizedOperator === 'within_next' || normalizedOperator === 'older_than') {
        const amount = Number(value?.value);
        const unit = normalizeDurationUnit(value?.unit);
        if (!Number.isFinite(amount) || amount <= 0 || !unit) return null;
        return { value: amount, unit };
      }

      if (normalizedOperator === 'is_this') {
        const unit = normalizeCalendarUnit(value?.unit);
        return unit ? { value: null, unit } : null;
      }

      if (normalizedOperator === 'is_overdue' || normalizedOperator === 'is_today') {
        return null;
      }
    }

    if (fieldType === 'daterange' || fieldType === 'datetimerange') {
      if (normalizedOperator === 'overlaps_past' || normalizedOperator === 'overlaps_next') {
        const amount = Number(value?.value);
        const unit = normalizeDurationUnit(value?.unit);
        if (!Number.isFinite(amount) || amount <= 0 || !unit) return null;
        return { value: amount, unit };
      }

      if (normalizedOperator === 'overlaps_this') {
        const unit = normalizeCalendarUnit(value?.unit);
        return unit ? { value: null, unit } : null;
      }

      if (normalizedOperator === 'is_active') {
        return null;
      }
    }

    if (value == null) return '';
    return String(value).trim();
  }

  if (fieldType === 'boolean') {
    if (value === true || value === 'true') return true;
    if (value === false || value === 'false') return false;
    return null;
  }

  if (fieldType === 'checklist') {
    if (
      normalizedOperator === 'is_empty'
      || normalizedOperator === 'is_not_empty'
      || normalizedOperator === 'is_complete'
      || normalizedOperator === 'is_not_complete'
    ) {
      return null;
    }

    if (
      normalizedOperator === 'item_is_done'
      || normalizedOperator === 'item_is_not_done'
    ) {
      const raw = Array.isArray(value) ? (value[0] ?? '') : value;
      const normalized = String(raw ?? '').trim();
      if (!normalized) return '';

      const exactValue = enumOptions.find((option) => option.value === normalized);
      if (exactValue) return exactValue.value;

      const labelMatch = enumOptions.find((option) => (
        String(option.label || '').trim().toLowerCase() === normalized.toLowerCase()
      ));
      if (labelMatch) return labelMatch.value;

      return normalized;
    }

    return null;
  }

  if (fieldType === 'number' || fieldType === 'money' || fieldType === 'percentage') {
    if (normalizedOperator === 'between') {
      const min = Number(value?.min);
      const max = Number(value?.max);
      if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
      if (min > max) return null;
      return { min, max };
    }

    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  if (isEnumCompatibleFieldType(fieldType)) {
    const normalizeEnumSingle = (rawEntry) => {
      const normalized = String(rawEntry ?? '').trim();
      if (!normalized) return '';

      const exactValue = enumOptions.find((option) => option.value === normalized);
      if (exactValue) return exactValue.value;

      const labelMatch = enumOptions.find((option) => (
        String(option.label || '').trim().toLowerCase() === normalized.toLowerCase()
      ));
      if (labelMatch) return labelMatch.value;

      return '';
    };

    if (normalizedOperator === 'is_any_of' || normalizedOperator === 'is_none_of') {
      const rawItems = Array.isArray(value)
        ? value
        : String(value ?? '').split(',');

      const unique = [];
      const seen = new Set();
      rawItems.forEach((entry) => {
        const normalized = normalizeEnumSingle(entry);
        if (!normalized) return;

        if (seen.has(normalized)) return;
        seen.add(normalized);
        unique.push(normalized);
      });

      return unique;
    }

    const singleValue = Array.isArray(value) ? (value[0] ?? '') : value;
    return normalizeEnumSingle(singleValue);
  }

  if (fieldType === 'email') {
    return normalizeEmailValueForStorage(value);
  }

  if (fieldType === 'phone') {
    return normalizePhoneValueForStorage(value);
  }

  if (fieldType === 'tags') {
    const rawItems = Array.isArray(value)
      ? value
      : String(value ?? '').split(',');

    const unique = [];
    const seen = new Set();
    rawItems.forEach((entry) => {
      const normalized = String(entry ?? '').trim();
      if (!normalized) return;

      const key = normalized.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      unique.push(normalized);
    });

    return unique;
  }

  if (fieldType === 'people') {
    const rawItems = Array.isArray(value)
      ? value
      : String(value ?? '').split(',');

    const unique = [];
    const seen = new Set();

    rawItems.forEach((entry) => {
      const key = typeof entry === 'string' || typeof entry === 'number'
        ? String(entry).trim()
        : String(entry?.userKey || entry?.key || '').trim();
      if (!key) return;

      const dedupeKey = key.toLowerCase();
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);

      if (entry && typeof entry === 'object') {
        unique.push({
          ...entry,
          key,
          userKey: key,
        });
        return;
      }

      unique.push({ key, userKey: key });
    });

    return unique;
  }

  if (fieldType === 'richtext') {
    return createRichTextValue(String(value ?? '').trim());
  }

  if (fieldType === 'url' || fieldType === 'string' || fieldType === 'text' || fieldType === 'longtext' || fieldType === 'textarea') {
    return String(value ?? '').trim();
  }

  return value ?? '';
};

export const getAllowedValueModes = (fieldType) => {
  if (fieldType === 'date' || fieldType === 'datetime' || fieldType === 'daterange' || fieldType === 'datetimerange') {
    return ['literal', 'now', 'null'];
  }

  if (isEnumCompatibleFieldType(fieldType)) {
    return ['literal', 'null'];
  }

  if (fieldType === 'tags') {
    return ['literal', 'add', 'remove', 'null'];
  }

  if (fieldType === 'people') {
    return ['literal', 'add', 'remove', 'null'];
  }

  return ['literal', 'null'];
};
